/** English UI strings — source of truth for `UiKey`. */
export const enMessages = {
  "meta.description":
    "DMeterLine — free AWS and LLM cost calculator: estimate monthly cloud spend (Lambda, API Gateway, DynamoDB, RDS, tokens) before you ship. Indicative planning for startups and product teams.",
  "meta.keywords":
    "AWS cost calculator, AWS pricing estimate, cloud cost calculator, serverless cost estimate, AWS monthly bill estimate, LLM cost calculator, OpenAI API cost, GPT API pricing, token cost estimate, Lambda cost, DynamoDB cost, RDS pricing, API Gateway cost, CloudFront cost, EKS EC2 estimate, infrastructure budget, pre-launch cloud cost, SaaS hosting cost, FinOps planning, DMeterLine",
  "lang.switch.uk": "UA",
  "lang.switch.en": "EN",
  "lang.switch.es": "ES",
  "lang.aria": "Interface language",

  "hero.eyebrow": "Before you build → know how much it will cost",
  "hero.title": "DMeterLine",
  "hero.lede.before": "Describe the feature in plain text. Parsing → structure. ",
  "hero.lede.strong": "Formulas",
  "hero.lede.after": " compute AWS + LLM — not model guesswork.",
  "hero.disclaimer":
    "Indicative planning only — not actual billing from AWS or other providers.",

  "accuracy.title": "How accurate is the estimate?",
  "accuracy.lead":
    "Use it to compare options and get the right order of magnitude — not as a forecast of your real AWS or LLM invoice.",
  "accuracy.pt1":
    "The model simplifies architecture (Lambda duration, DynamoDB patterns, blended token pricing, etc.) and does not see your real traffic.",
  "accuracy.pt2":
    "Pricing uses public ballpark rates — not your discounts, Reserved capacity, Free Tier, or line-item detail from Cost Explorer.",
  "accuracy.pt3":
    "Region and deployment stack use multipliers and typical configs, not an exact match to your account.",
  "accuracy.pt4":
    "The “actual bill” field is there to sanity-check the model against what you really pay.",
  "accuracy.bandsSimple":
    "Simple mode: the range under the total is deliberately wide (~0.48×–1.92× the point estimate) to reflect uncertainty.",
  "accuracy.bandsTechnical":
    "Technical mode: a tighter band (~0.86×–1.14×) around the same model — still indicative, not a quote or SLA.",

  "mode.planning": "Planning",
  "mode.description": "Description",
  "mode.aria": "How you define workload",

  "planning.title": "Workload parameters",
  "planning.hint":
    "Direct numbers and features → same workload shape as after parsing. Costs use formulas.",
  "planning.usersDaily": "Daily active users (DAU)",
  "planning.requestsPerUser": "Actions / API calls per user per day",
  "planning.features": "Architecture features",
  "planning.feat.chat": "Chat / messaging",
  "planning.feat.storage": "Data storage",
  "planning.feat.search": "Search / history",
  "planning.feat.ai": "AI / LLM in requests",
  "planning.feat.realtime": "Realtime / WebSocket",
  "planning.feat.files": "Files / object storage",
  "planning.aiSection": "LLM parameters",
  "planning.promptTokens": "Avg prompt tokens",
  "planning.completionTokens": "Avg completion tokens",
  "planning.aiShare": "Share of requests that hit the LLM",

  sampleInput: `Chat app:
- 10k users daily
- 100 messages per user
- messages stored
- search history
- AI responses via GPT`,

  "form.title": "Feature description",
  "form.hint":
    "MVP: heuristic parser (no API). Later the same JSON from an LLM in Lambda.",
  "form.submit": "Parse → workload",
  "form.tryPlanningTemplates": "Quick templates & sliders",
  "form.tryPlanningHint":
    "Skip typing — POC, MVP, chat app, and more in the Planning tab.",
  "form.jsonLabel": "Structured workload (rules input)",

  "breakdown.title": "💰 Cost breakdown",
  "breakdown.total": "Total (indicative)",
  "breakdown.perMonth": "/ mo",

  "whatif.title": "What-if",
  "whatif.subPlanning":
    "Multipliers apply to the baseline workload from the form (fields stay the same).",
  "whatif.subDescription":
    "Multipliers apply after text parse (they do not edit the text field).",
  "whatif.users": "Users / day:",
  "whatif.requests": "Actions / user:",
  "whatif.liveTotal": "Live total",

  "scaling.title": "📈 Scaling (base workload × users)",
  "scaling.sub": "Curve from baseline workload; axes — users / day and $/mo.",
  "scaling.chartAria": "Scaling cost chart",
  "scaling.barTitle": "{{users}} users → {{usd}} USD/mo",
  "scaling.legend": "Now: ~{{dau}} DAU — closest bar highlighted",

  "cp.title": "Cost vs performance",
  "cp.cheaper": "Cheaper",
  "cp.balanced": "Balanced",
  "cp.faster": "Faster",

  "rec.risks": "⚠️ Risks",
  "rec.risksEmpty": "No critical flags with the current rules.",
  "rec.suggestions": "🔧 Optimizations",
  "rec.effort": "effort:",
  "rec.arch": "🏗️ Architecture hints",

  "footer.note": "Indicative estimates — verify against real AWS/OpenAI bills.",
  "footer.legal":
    "DMeterLine provides indicative spend forecasts based on simplified assumptions and public price baselines only. It is not an official quote, commercial offer, cost guarantee, or tax, accounting, or legal advice. Actual charges depend on provider pricing, region, discounts, free tiers, real usage, and changing terms. The operator is not liable for decisions, contracts, or losses made solely on the basis of this tool.",
  "export.legalHeading": "Disclaimer",
  "footer.contactLead": "Licensing, enterprise, or purchase — ",
  "footer.contactAria": "Send email",

  "title.suffix": " — before you build",

  "error.title": "Something went wrong",
  "error.body":
    "Try refreshing the page. If it keeps happening, check the console or send this text to support.",
  "error.retry": "Reload",

  "effort.low": "low",
  "effort.medium": "medium",
  "effort.high": "high",

  "toolbar.region": "AWS region (multiplier)",
  "toolbar.regionHelp":
    "Rough factor vs us-east-1; all breakdown amounts scale with the selected region.",
  "toolbar.budgetCap": "Monthly budget (USD, optional)",
  "toolbar.budgetPlaceholder": "e.g. 500",
  "toolbar.exportTitle": "Report",
  "share.title": "Scenario",
  "share.hint": "Share a link or JSON file — no AWS account needed.",
  "share.copyLink": "Copy link",
  "share.linkCopied": "Link copied",
  "share.linkTooLong": "Scenario is too large for a link — use Download JSON.",
  "share.exportJson": "Download JSON",
  "share.importJson": "Import JSON",
  "share.importAria": "Import scenario from JSON file",
  "share.importOk": "Scenario loaded.",
  "share.importInvalid": "Invalid JSON or unsupported version.",
  "region.af-south-1": "af-south-1 — Cape Town",
  "region.ap-east-1": "ap-east-1 — Hong Kong",
  "region.ap-northeast-1": "ap-northeast-1 — Tokyo",
  "region.ap-northeast-2": "ap-northeast-2 — Seoul",
  "region.ap-northeast-3": "ap-northeast-3 — Osaka",
  "region.ap-south-1": "ap-south-1 — Mumbai",
  "region.ap-south-2": "ap-south-2 — Hyderabad",
  "region.ap-southeast-1": "ap-southeast-1 — Singapore",
  "region.ap-southeast-2": "ap-southeast-2 — Sydney",
  "region.ap-southeast-3": "ap-southeast-3 — Jakarta",
  "region.ap-southeast-4": "ap-southeast-4 — Melbourne",
  "region.ca-central-1": "ca-central-1 — Montreal",
  "region.eu-central-1": "eu-central-1 — Frankfurt",
  "region.eu-central-2": "eu-central-2 — Zurich",
  "region.eu-north-1": "eu-north-1 — Stockholm",
  "region.eu-south-1": "eu-south-1 — Milan",
  "region.eu-south-2": "eu-south-2 — Spain",
  "region.eu-west-1": "eu-west-1 — Ireland",
  "region.eu-west-2": "eu-west-2 — London",
  "region.eu-west-3": "eu-west-3 — Paris",
  "region.il-central-1": "il-central-1 — Tel Aviv",
  "region.me-central-1": "me-central-1 — UAE",
  "region.me-south-1": "me-south-1 — Bahrain",
  "region.sa-east-1": "sa-east-1 — São Paulo",
  "region.us-east-1": "us-east-1 — N. Virginia (US)",
  "region.us-east-2": "us-east-2 — Ohio",
  "region.us-west-1": "us-west-1 — N. California",
  "region.us-west-2": "us-west-2 — Oregon",

  "budget.over":
    "Indicative total {{total}} exceeds your cap {{cap}} / mo. Check what-if and optimizations.",

  "export.copy": "Copy Markdown",
  "export.download": "Download .md",
  "export.downloadHtml": "Download HTML (with charts)",
  "export.printHint": "Open this file in a browser. For PDF: Print → Save as PDF.",
  "export.colItem": "Item",
  "export.colPct": "Share",
  "export.colUsd": "USD / mo",
  "export.workloadSection": "Workload (JSON)",
  "export.assumptionsHeading": "Model assumptions",
  "export.copied": "Copied",
  "export.failed": "Copy failed",
  "export.downloadPdf": "Download PDF",
  "export.pdfBusy": "Building PDF…",
  "export.pdfError": "Could not build PDF. Try HTML → Print to PDF in your browser.",
  "export.appendix.trustTitle": "Trust, versioning & audit",
  "export.appendix.modelVersion": "Model version",
  "export.appendix.schemaVersion": "Scenario schema version",
  "export.appendix.coefficientsDate": "Coefficients last updated",
  "export.appendix.referencesTitle": "Reference pricing (assumptions)",
  "export.appendix.snapshotTitle": "Estimation snapshot (levers at export time)",
  "export.appendix.region": "AWS region",
  "export.appendix.audience": "View / audience mode",
  "export.appendix.llmProfile": "LLM price profile",
  "export.appendix.computeProfile": "Compute profile",
  "export.appendix.displayCurrency": "Display currency in UI",
  "export.appendix.budgetCap": "Budget cap",
  "export.appendix.budgetNone": "None",
  "export.appendix.savingsOn": "Savings levers enabled",
  "export.appendix.savingsNone": "None enabled",
  "export.appendix.stackTitle": "Deployment stack",
  "export.appendix.notesTitle": "Scenario notes",
  "export.appendix.disclaimer":
    "Totals in this report are modeled in USD from public list prices and heuristics; EUR display (if any) is indicative FX only.",
  "export.readonlyBanner":
    "Read-only cost snapshot — open the main app to edit assumptions. Model outputs are indicative, not an invoice.",
  "export.copyNotion": "Copy Notion-friendly Markdown",
  "export.downloadReadonlyHtml": "Download read-only HTML",
  "export.downloadPackMd": "Download scenarios (.md pack)",
  "export.downloadPackJson": "Download scenarios (.json pack)",
  "export.notionCopied": "Notion block copied",

  "trust.panelTitle": "Why trust these numbers?",
  "trust.panelLead":
    "We version the model and schema, date coefficient changes, and cite public pricing sources. Exports include an audit of region, stack, and toggles used.",
  "trust.coefficientsLabel": "Coefficients updated",
  "trust.link.calculator": "AWS Pricing Calculator",
  "trust.link.pricing": "AWS public pricing",
  "trust.link.docs": "AWS Billing overview",

  "enterprise.title": "Enterprise (coming soon)",
  "enterprise.lead":
    "Short stable share links (backend), Slack / webhooks, synced scenario notes, Cost Explorer CSV sync, and deeper SSO — on the roadmap.",
  "enterprise.b1": "Stable short links & org workspaces",
  "enterprise.b2": "Webhook / Slack scenario summary",
  "enterprise.b3": "Notion / Confluence push (API)",
  "enterprise.b4": "Scenario comments with team sync",
  "enterprise.b5": "CSV / CUR ingestion for actual vs model",

  "share.previewOpen": "Open read-only preview",
  "share.scenarioNotesLabel": "Scenario notes (local + JSON export)",
  "share.scenarioNotesPlaceholder": "Assumptions, stakeholder context, links…",

  "preview.title": "Scenario preview",
  "preview.subtitle":
    "Read-only totals from the shared hash — nothing is written to this browser until you open the full app.",
  "preview.invalid":
    "Missing or invalid scenario link. Use “Copy link” from the main app or a full JSON import.",
  "preview.openFull": "Open in full editor",

  "toolbar.displayCurrency": "Display currency",
  "toolbar.currencyUsd": "USD (model)",
  "toolbar.currencyEur": "EUR (indicative)",
  "toolbar.currencyEurHint":
    "~0.92 EUR per 1 USD for display only; the model stays USD-based.",

  "csv.importButton": "Import Cost Explorer CSV (total)",
  "csv.importHint":
    "Rough sum from an AWS CSV export — fills “actual bill” and, when the file has a service column, a line-by-line comparison below.",
  "csv.importOk": "Parsed total from CSV.",
  "csv.importBad": "Could not parse CSV totals.",
  "csv.compare.title": "Bill vs model (by category)",
  "csv.compare.hint":
    "CSV service names are mapped heuristically to this model’s lines — not a forensic match to Cost Explorer.",
  "csv.compare.colLine": "Line",
  "csv.compare.colModel": "Model / mo",
  "csv.compare.colCsv": "CSV / mo",
  "csv.compare.colDelta": "Δ vs model",
  "csv.compare.unmapped": "Other (unmapped services)",
  "csv.compare.clear": "Clear CSV breakdown",

  "namedScenarios.packHint":
    "Export all named snapshots plus the current scenario as one Markdown table or JSON array.",
  "namedScenarios.packColScenario": "Scenario",
  "namedScenarios.packColTotal": "Monthly total (USD)",
  "namedScenarios.currentLabel": "Current (editor)",

  "nav.footerAria": "Site",
  "nav.home": "Estimator",
  "nav.howItWorks": "How it works",
  "nav.privacy": "Privacy",
  "nav.terms": "Terms",
  "nav.sidebarAria": "Main navigation",
  "nav.menuOpen": "Open menu",
  "nav.menuClose": "Close menu",
  "nav.collapseSidebar": "Hide sidebar",
  "nav.expandSidebar": "Show sidebar",
  "nav.sidebarTagline": "Ballpark AWS & LLM — before you ship.",
  "nav.sidebarStripAria": "Highlights",
  "nav.sidebarPill1": "Formula-based",
  "nav.sidebarPill2": "AWS + LLM",
  "nav.sidebarPill3": "Export & share",
  "nav.viewModeHint":
    "Simple / Technical view: use the VIEW switch in the left menu (open the sidebar if it’s hidden).",
  "scrollTop.aria": "Scroll to top",

  "doc.how.title": "How DMeterLine works",
  "doc.how.body": `DMeterLine turns a short feature description or planning numbers into a structured workload (users, requests, features, optional LLM usage), then applies simplified public-style price baselines for AWS-style services and LLM tokens.

The output is indicative only: it is not your AWS invoice, not a quote, and not legal or tax advice. Regions apply a rough multiplier vs a us-east-1 baseline; deployment stack choices add or remove major cost lines (API Gateway, Lambda, DynamoDB, RDS, CloudFront, etc.).

Use “Compare with actual bill” to see how far a real monthly total is from the model. Export reports or share JSON links when you need to align with teammates — no cloud account is required to use the tool.

We improve formulas and copy over time; always verify numbers against real provider bills before commitments.`,

  "doc.privacy.title": "Privacy",
  "doc.privacy.body": `DMeterLine runs as a client-side web app. Scenario data you enter is stored in your browser (localStorage) so the page can remember your inputs. We do not operate a login or user database in this version.

If you enable optional analytics (e.g. Plausible) via site configuration, the provider may process basic page statistics according to their policy — typically without cookies.

Do not paste secrets, credentials, or personal data into the estimator. Shared links and JSON exports encode your scenario; treat them like any sensitive planning document.

Contact the operator via the email in the footer for privacy questions or data-related requests.`,

  "doc.terms.title": "Terms of use",
  "doc.terms.body": `By using DMeterLine you agree that all figures are estimates based on simplified assumptions and public price hints. The operator does not guarantee accuracy, fitness for a particular purpose, or that results match future bills.

The tool is not a substitute for professional architecture, security, compliance, tax, accounting, or legal advice. You are responsible for decisions made using these estimates.

The service is provided “as is” without warranties to the extent permitted by law. Liability is limited to the maximum extent permitted for a free planning tool of this kind.

These terms may be updated; continued use after changes means you accept the updated terms.`,

  "meta.ogTitleSuffix": "indicative AWS & LLM cost planning",

  "breakdown.linesMonthlyHint":
    "Line items below are always shown per month; only the summary total follows your selected view.",
  "breakdown.showTotalAs": "Show summary total as",
  "breakdown.unit.month": "/ month",
  "breakdown.unit.year": "/ year",
  "breakdown.unit.perDau": "/ DAU / month",
  "breakdown.unit.perMillionReq": "/ 1M requests",
  "breakdown.unitSuffix.month": "/ mo (same as lines)",
  "breakdown.unitSuffix.year": "/ yr (×12)",
  "breakdown.unitSuffix.perDau": "/ active user / mo",
  "breakdown.unitSuffix.perMillionReq": "/ 1M requests / mo",

  "llmProfile.title": "LLM price profile",
  "llmProfile.hint":
    "Rough tier vs the baseline token rates (not a live API quote). Stacks with the “cheaper LLM tier” savings lever.",
  "llmProfile.economy": "Economy",
  "llmProfile.standard": "Standard",
  "llmProfile.premium": "Premium",

  "computeProfile.title": "Workload compute profile",
  "computeProfile.hint":
    "CPU: baseline Lambda/egress proxy. GPU-style: higher Lambda and data-transfer multipliers (heuristic — not dedicated GPU instance pricing).",
  "computeProfile.cpu": "CPU / API",
  "computeProfile.gpu": "GPU-style",

  "regionCompare.title": "Same scenario, all regions",
  "regionCompare.hint":
    "Indicative totals with the current workload, stack, savings, LLM profile, and compute profile. Your selected region is highlighted.",
  "regionCompare.colRegion": "Region",
  "regionCompare.colTotal": "Total / mo",
  "regionCompare.colDelta": "vs selected",
  "regionCompare.deltaFoot":
    "Δ% is total in that region vs your currently selected region (same workload and model settings).",

  "actualBill.title": "Compare with actual bill",
  "actualBill.hint":
    "Optional: enter what you really paid last month (USD) to see % difference vs this model (indicative).",
  "actualBill.label": "Actual monthly total (USD)",
  "actualBill.placeholder": "e.g. 842",
  "actualBill.invalid": "Enter a positive number.",
  "actualBill.vsModel":
    "Actual {{actual}} vs model {{model}} — {{pct}}% vs model (positive = you paid more than the estimate).",

  "namedScenarios.title": "Named snapshots",
  "namedScenarios.hint":
    "Save up to 5 named scenarios in this browser (same data as share/JSON). Load one to replace the current form.",
  "namedScenarios.namePlaceholder": "Name (e.g. Prod Q2)",
  "namedScenarios.save": "Save current",
  "namedScenarios.load": "Load",
  "namedScenarios.delete": "Remove",
  "namedScenarios.empty": "No saved scenarios yet.",
  "namedScenarios.max": "Maximum 5 entries; saving adds on top and drops the oldest.",

  "savings.title": "Savings levers (indicative)",
  "savings.hint":
    "Enable Spot, committed Dynamo, cheaper LLM, or weekend shutdown — totals and charts update.",
  "savings.spot": "Spot / preemptible compute",
  "savings.spotHelp":
    "~58% off Lambda GB·s only (request charges unchanged). For Batch, Fargate Spot, etc.",
  "savings.dynamo": "Committed / provisioned DynamoDB",
  "savings.dynamoHelp": "~22% off the Dynamo line with steady traffic.",
  "savings.llm": "Cheaper LLM tier",
  "savings.llmHelp": "~28% off LLM: batch API, smaller model, response cache.",
  "savings.llmBatch": "LLM batch / async API (vs realtime)",
  "savings.llmBatchHelp":
    "Extra ~32% off the LLM line after economy tier — async batch-style pricing vs interactive API.",
  "savings.weekends": "Off weekends (dev / staging)",
  "savings.weekendsHelp":
    "EC2, EKS worker, and RDS instance hours in the model ×5/7. Lambda, storage, DNS, GuardDuty unchanged. Not for 24/7 prod.",

  "stack.title": "Deployment stack (indicative)",
  "stack.hint":
    "Frontend, backend, and database drive breakdown. Below: optional Route 53, ALB+WAF, GuardDuty. Pricing is simplified us-east-1 × region.",
  "stack.frontend": "Frontend / edge",
  "stack.frontend.apigw": "API Gateway only (typical serverless)",
  "stack.frontend.cloudfront": "CloudFront (CDN)",
  "stack.frontend.ec2_spa": "SPA on EC2 (nginx)",
  "stack.backend": "Backend / compute",
  "stack.backend.lambda": "AWS Lambda",
  "stack.backend.ec2": "EC2 (ASG indicative)",
  "stack.backend.eks": "Amazon EKS",
  "stack.database": "Primary database in model",
  "stack.database.dynamo": "DynamoDB only",
  "stack.database.rds": "RDS (primary)",
  "stack.database.both": "DynamoDB + RDS (split)",
  "stack.ec2AutoCount": "EC2 count from DAU (auto)",
  "stack.ec2Class": "Backend instance type",
  "stack.ec2Count": "Instance count (manual)",
  "stack.rdsAutoSize": "RDS class from DAU (auto)",
  "stack.rdsClass": "RDS class (manual)",
  "stack.rdsStorage": "RDS storage (GB, gp3)",
  "stack.rdsMultiAz": "Multi-AZ RDS in formula",
  "stack.network": "Service exposure",
  "stack.network.public": "Public (internet-facing API)",
  "stack.network.private": "Private (VPN / DX / corporate)",
  "stack.networkHelp":
    "Private means no public app backend; adds NAT/VPC endpoint-style cost line and plan text about endpoint/data egress.",
  "stack.extrasTitle": "Add-ons (as needed)",
  "stack.route53": "Route 53 — domain & DNS",
  "stack.route53Help":
    "Hosted zone + queries. ACM public certs for ALB/CloudFront/API Gateway are usually free.",
  "stack.albWaf": "ALB + AWS WAF",
  "stack.albWafHelp":
    "Indicative L7 balancer, WAF ACL, and request charges. Managed rule groups are on top.",
  "stack.guardDuty": "Amazon GuardDuty",
  "stack.guardDutyHelp":
    "Threat detection (VPC/DNS/CloudTrail) — simplified line; multi-account detail is separate.",

  "load.title": "Approximate load",
  "load.hint": "From workload: requests, RPS, AI share — for scale sense, not SLA.",
  "load.hintSimple":
    "Approximate activity in plain terms — useful for planning, not a performance guarantee.",
  "bottleneck.title": "Likely bottlenecks",

  "preset.title": "Quick presets",
  "preset.poc": "POC / spike",
  "preset.mvp": "Early MVP",
  "preset.web_app": "Typical web app",
  "preset.mobile_backend": "Mobile app API",
  "preset.internal_tool": "Internal / admin tool",
  "preset.api_light": "Light API",
  "preset.chat_app": "Chat app",
  "preset.rag_search": "RAG / search",
  "preset.high_traffic_ai": "High traffic + AI",

  "sensitivity.title": "Sensitivity (+10%)",
  "sensitivity.dau": "DAU +10%",
  "sensitivity.req": "Actions / user +10%",
  "sensitivity.aiShare": "LLM request share +10%",
  "sensitivity.tokens": "Prompt & completion tokens +10%",

  "checklist.title": "Pre-launch checklist",
  "checklist.cache_responses": "Cache responses / idempotent APIs where possible",
  "checklist.token_budget": "Token limits and timeouts for LLM calls",
  "checklist.billing_alarms": "AWS billing alarms / budgets",
  "checklist.load_test": "Load test for expected peak",
  "checklist.cdn_static": "CDN for static assets and edge cache",
  "checklist.async_writes": "Async writes / queues for spikes",
  "checklist.review_reserved":
    "Review Reserved / Savings Plans after traffic stabilizes",

  "scenario.title": "Scenario compare A / B",
  "scenario.saveA": "Save as A",
  "scenario.saveB": "Save as B",
  "scenario.clearA": "Clear A",
  "scenario.clearB": "Clear B",
  "scenario.total": "Total / mo",
  "scenario.deltaAB": "Delta (B − A)",
  "scenario.deltaVsCurrent": "vs current estimate",
  "scenario.empty": "Save two snapshots to compare spend.",
  "scenario.snapshotMeta":
    "Base workload × users {{u}}×, actions {{r}}× → {{usd}} / mo",

  "plan.title": "Infrastructure plan (indicative)",
  "plan.subtitle":
    "What usually sits behind the numbers: VPC, edge, compute, and data — aligned with your deployment stack.",
  "plan.subtitleSimple":
    "Plain-language outline of your scenario and where spend usually goes. Switch to Technical for VPC, Lambda, databases, and security in detail.",

  "audience.label": "View",
  "audience.aria": "Analysis detail level",
  "audience.simple": "Simple",
  "audience.technical": "Technical",
  "audience.hintSimple":
    "Wider cost range and non-technical wording — best for budgeting conversations with stakeholders.",
  "audience.hintTechnical":
    "Tighter indicative band, RPS-style load, and engineer-oriented risks and plan.",
  "audience.hintSimpleSidebar": "Plain language and a wider cost range.",
  "audience.hintTechnicalSidebar": "Tighter range and engineering-oriented detail.",

  "breakdown.rangeBandSimple":
    "Indicative range (wide): {{low}} – {{high}} — real bills vary with discounts, region, and usage.",
  "breakdown.rangeBandTechnical":
    "Tighter indicative band: {{low}} – {{high}} — still not a quote; assumes in the model apply.",
  "breakdown.lineRange":
    "Indicative band for this line: {{low}} – {{high}} (same uncertainty mode as the total).",

  "glossary.summary": "Glossary: DAU, GB·s, egress…",
  "glossary.termDau": "DAU",
  "glossary.defDau":
    "Daily active users — people who use the app at least once on a typical day. The model scales requests and storage from this count.",
  "glossary.termGb": "GB·s (gigabyte-seconds)",
  "glossary.defGb":
    "Lambda billing unit: function memory size × how long it runs. More memory or longer handlers increases this line even if request count stays flat.",
  "glossary.termEgress": "Data transfer (egress)",
  "glossary.defEgress":
    "Outbound data from AWS to the internet (or between regions). API responses, downloads, and streaming can add egress not shown as a separate vendor line on small bills.",

  "whatif.subAudienceSimple":
    "Numbers below scale your scenario; the total is a midpoint inside the wide range shown in the breakdown.",
  "whatif.subAudienceTechnical":
    "Multipliers apply to the baseline workload; sensitivity cards show +10% DAU/request deltas on the same model.",

  "flow.section.model": "Cost assumptions",
  "flow.section.results": "Breakdown & mix",
  "flow.detailsAccuracy": "About accuracy — optional details",
  "flow.detailsModel": "Optional: savings, AI tier & deployment stack",
  "flow.detailsExport": "Export & share (for your team)",
  "flow.detailsExpert": "Advanced: regions, scaling, scenarios & sensitivity",

  "livePulse.title": "Live estimate",
  "livePulse.sub":
    "Totals and bars react immediately to sliders, savings toggles, LLM tier, and deployment stack.",
  "livePulse.sparkCaption": "Session trend (monthly total)",
  "livePulse.sparkAria": "Line chart of estimated monthly total during this session",
  "livePulse.topCaption": "Top cost lines",
  "livePulse.empty": "Configure a workload above to see live totals and trends.",

  "mix.title": "Cost mix (share)",
  "mix.pieHint":
    "Donut = share of the monthly total per line (same colors as the bar and breakdown). Hover a slice for USD and %.",
  "mix.pieAria": "Donut chart of monthly cost by line item",
  "mix.barAria": "Stacked bar of monthly cost by line item",

  "theme.aria": "Color theme",
  "theme.light": "Light",
  "theme.dark": "Dark",
} as const;

export type UiKey = keyof typeof enMessages;
